import { ProjectUser } from '@monorepo/shared/apiClient/types';
import { Button } from '@monorepo/shared/componentsV2/Button';
import { Description } from '@monorepo/shared/componentsV2/Description';
import { MultiSelectField } from '@monorepo/shared/componentsV2/fields/MultiSelectField';
import { BaseModal } from '@monorepo/shared/componentsV2/modals/BaseModal';
import { ModalFormInstructions } from '@monorepo/shared/componentsV2/modals/contents/ModalFormInstructions';
import { useToast } from '@monorepo/shared/contexts/ToastContext';
import { useCurrentProject } from '@monorepo/shared/hooks/useCurrentProject';
import { useCurrentUser } from '@monorepo/shared/hooks/useCurrentUser';
import { useOrgProjectsWithUsers } from '@monorepo/shared/hooks/useOrgProjectsWithUsers';
import { useProjectUserOptions } from '@monorepo/shared/hooks/useProjectUserOptions';
import { isMapistryEmail } from '@monorepo/shared/utils/isMapistryEmail';
import { Permissions } from 'mapistry-shared';
import React, { useCallback, useMemo } from 'react';
import { Form } from 'react-final-form';
import { useReassignTasks } from '../hooks/useReassignTasks';
import { getValidateNextAssigneeIds } from './reassignTasksUtils';

type ReassignTasksFormValues = {
  nextAssigneeIds: string[];
};

const INITIAL_VALUES: ReassignTasksFormValues = { nextAssigneeIds: [] };

interface ReassignTasksModalProps {
  organizationId: string;
  projectId: string;
  user: ProjectUser;
  onClose: () => void;
}

export function ReassignTasksModal(props: ReassignTasksModalProps) {
  const { organizationId, projectId, user, onClose } = props;

  const { showUserFriendlyErrorToast, success } = useToast();
  const [reassignTasks] = useReassignTasks({ config: { throwOnError: true } });

  const { currentUser } = useCurrentUser();
  const currentUserEmail = currentUser?.email;

  const { project } = useCurrentProject();
  const projectName = project?.name;

  const {
    userOptions,
    isLoading: isLoadingUserOptions,
    error: userOptionsError,
  } = useProjectUserOptions({
    organizationId,
    projectId,
    includeMapistryUsers: currentUserEmail
      ? isMapistryEmail(currentUserEmail)
      : false,
  });

  const {
    projectsWithUsers,
    isLoading: isLoadingAssignableUsers,
    error: assignableUsersError,
  } = useOrgProjectsWithUsers({
    organizationId,
    permission: Permissions.ASSIGN_TASKS_TO,
  });

  const assigneeOptions = useMemo(() => {
    if (!userOptions || !projectsWithUsers) return [];
    const usersWithAssignToPermissions = new Set(
      projectsWithUsers.find((p) => p.id === projectId)?.userIds ?? [],
    );
    return userOptions.filter((o) => usersWithAssignToPermissions.has(o.value));
  }, [projectId, projectsWithUsers, userOptions]);

  const isLoadingAssigneeOptions =
    isLoadingUserOptions || isLoadingAssignableUsers;

  const validateNextAssigneeIds = useMemo(
    () => getValidateNextAssigneeIds(user.userId),
    [user.userId],
  );

  const onSubmit = useCallback(
    async (values: ReassignTasksFormValues) => {
      try {
        await reassignTasks({
          projectId,
          previousAssigneeId: user.userId,
          nextAssigneeIds: values.nextAssigneeIds,
        });
        success(
          `Task assignments transferred. It may take a few minutes for all changes to be visible.`,
          { autoHideDuration: 5000 },
        );
        onClose();
      } catch (err) {
        showUserFriendlyErrorToast(
          err,
          `Unable to reassign tasks for ${user.userName}.`,
        );
      }
    },
    [
      onClose,
      projectId,
      reassignTasks,
      showUserFriendlyErrorToast,
      success,
      user,
    ],
  );

  const error = userOptionsError || assignableUsersError;
  if (error) {
    showUserFriendlyErrorToast(error);
    onClose();
    return null;
  }

  return (
    <Form<ReassignTasksFormValues>
      onSubmit={onSubmit}
      initialValues={INITIAL_VALUES}
      subscription={{ submitting: true, hasValidationErrors: true }}
    >
      {({ handleSubmit, submitting, hasValidationErrors }) => (
        <BaseModal
          open
          onClose={submitting ? undefined : onClose}
          showCloseButton
          title="Task reassignment"
          maxWidth="sm"
          buttons={[
            <Button
              key="close"
              variant="text"
              color="primary"
              onClick={onClose}
              disabled={submitting}
            >
              Close
            </Button>,
            <Button
              key="apply"
              variant="contained"
              color="secondary"
              onClick={handleSubmit}
              disabled={submitting || hasValidationErrors}
            >
              {submitting ? 'Applying...' : 'Apply'}
            </Button>,
          ]}
        >
          <ModalFormInstructions>
            Transfer tasks assigned to <b>{user.userName}</b> in{' '}
            <b>{projectName ?? 'this project'}</b> to:
          </ModalFormInstructions>
          <MultiSelectField
            name="nextAssigneeIds"
            label="Assign to"
            options={assigneeOptions}
            required
            validate={validateNextAssigneeIds}
            isReadOnly={submitting || isLoadingAssigneeOptions}
          />
          <Description>
            Tasks on other sites will not be affected by this action. Changes
            may take a few minutes to go live. If you need assistance, please
            contact your CSM.
          </Description>
        </BaseModal>
      )}
    </Form>
  );
}
